import { useState } from "react";
import axiosInstance from "@/hooks/axios";
import { isAxiosError } from "axios";
import { MembershipType } from "@/types/user";
import { useNavigate } from "react-router-dom";

interface CreateCheckoutSessionPayload {
  new_membership_name: MembershipType["name"];
}

interface CreateCheckoutSessionResponse {
  session_url?: string;
  message?: string;
}

export const useCreateStripeCheckoutSession = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckoutSession = async ({
    new_membership_name,
  }: CreateCheckoutSessionPayload): Promise<void> => {
    setLoading(true);
    setError(null);

    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setError("Not authenticated.");
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post<CreateCheckoutSessionResponse>(
        "users/users/checkout_session/",
        { new_membership_name },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      const { session_url, message } = response.data;

      if (message === "Membership downgraded to basic.") {
        navigate("/membership-success?redirect=profile");
      } else if (session_url) {
        window.location.href = session_url;
      } else {
        throw new Error("Stripe session URL not received.");
      }
    } catch (err) {
      if (isAxiosError(err)) {
        const message = err.response?.data?.error || err.message;
        setError(message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    createCheckoutSession,
    loading,
    error,
  };
};
